<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppointmentStore } from '@/stores/appointmentStore';

const emit = defineEmits<{ slotSelected: [] }>();

const { t } = useI18n();
const appointmentStore = useAppointmentStore();

const intlLocale = computed(() => 'fr-FR');

// Début de la fenêtre des 7 jours (au départ = aujourd'hui)
const currentWeekStart = ref(getStartOfDay(new Date()));

// Jour actif (index dans visibleDays)
const activeDayIndex = ref(0);

function getStartOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

const today = computed(() => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
});

// 7 prochains jours à partir de currentWeekStart (toujours à partir d'aujourd'hui ou plus)
const weekDays = computed(() => {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(currentWeekStart.value);
    d.setDate(d.getDate() + i);
    return d;
  });
});

// Jours affichés = uniquement ceux qui ont au moins un créneau
const visibleDays = computed(() =>
  weekDays.value.filter(d => getSlotsForDay(d).length > 0)
);

const weekEnd = computed(() => {
  const d = new Date(currentWeekStart.value);
  d.setDate(d.getDate() + 6);
  return d;
});

// Jour actif = parmi les visibleDays
const activeDay = computed(() => visibleDays.value[activeDayIndex.value] ?? null);

watch(currentWeekStart, () => {
  activeDayIndex.value = 0;
  loadWeekSlots();
});

// Quand les slots chargent, sélectionner auto le premier jour qui a des créneaux
watch(() => appointmentStore.slots, () => {
  const firstWithSlots = visibleDays.value.findIndex(d => getSlotsForDay(d).length > 0);
  if (firstWithSlots !== -1) {
    activeDayIndex.value = firstWithSlots;
  }
}, { flush: 'post' });

async function loadWeekSlots() {
  const start = formatDate(currentWeekStart.value);
  const end = formatDate(weekEnd.value);
  await appointmentStore.fetchSlots(start, end);
}

onMounted(() => {
  currentWeekStart.value = getStartOfDay(new Date());
  loadWeekSlots();
});

function prevWeek() {
  const d = new Date(currentWeekStart.value);
  d.setDate(d.getDate() - 7);
  if (d >= today.value) {
    currentWeekStart.value = d;
  }
}

function nextWeek() {
  const d = new Date(currentWeekStart.value);
  d.setDate(d.getDate() + 7);
  currentWeekStart.value = d;
}

const canGoPrev = computed(() => currentWeekStart.value > today.value);

// Regrouper les créneaux par jour
const slotsByDay = computed(() => {
  const map: Record<string, string[]> = {};
  for (const slot of appointmentStore.slots) {
    const day = new Date(slot.datetime).toISOString().split('T')[0];
    if (!map[day]) map[day] = [];
    map[day].push(slot.datetime);
  }
  return map;
});

function getSlotsForDay(day: Date): string[] {
  return slotsByDay.value[formatDate(day)] || [];
}

function formatTime(datetime: string): string {
  return new Date(datetime).toLocaleTimeString(intlLocale.value, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Paris'
  });
}

function formatDayTab(day: Date): { weekday: string; date: string } {
  return {
    weekday: day.toLocaleDateString(intlLocale.value, { weekday: 'short' }),
    date: day.toLocaleDateString(intlLocale.value, { day: 'numeric', month: 'short' })
  };
}

function isToday(day: Date): boolean {
  return day.toDateString() === new Date().toDateString();
}

function selectSlot(datetime: string) {
  appointmentStore.selectSlot(datetime);
  // Passer directement à l'étape 2
  emit('slotSelected');
}

const weekLabel = computed(() => {
  const loc = intlLocale.value;
  const start = currentWeekStart.value.toLocaleDateString(loc, { day: 'numeric', month: 'long' });
  const end = weekEnd.value.toLocaleDateString(loc, { day: 'numeric', month: 'long', year: 'numeric' });
  return `${start} – ${end}`;
});

const activeDaySlots = computed(() => activeDay.value ? getSlotsForDay(activeDay.value) : []);
</script>

<template>
  <div class="week-calendar">
    <!-- Navigation semaine -->
    <div class="week-nav">
      <button
        type="button"
        class="week-nav-btn"
        :disabled="!canGoPrev"
        @click="prevWeek"
        :title="t('booking.calendar.prevWeekTitle')"
      >
        <i class="fas fa-chevron-left"></i>
      </button>
      <span class="week-label">{{ weekLabel }}</span>
      <button
        type="button"
        class="week-nav-btn"
        @click="nextWeek"
        :title="t('booking.calendar.nextWeekTitle')"
      >
        <i class="fas fa-chevron-right"></i>
      </button>
    </div>

    <!-- Chargement -->
    <div v-if="appointmentStore.isLoading" class="calendar-loading">
      <i class="fas fa-spinner fa-spin"></i>
      <span>{{ t('booking.calendar.loadingSlots') }}</span>
    </div>

    <template v-else-if="visibleDays.length > 0">
      <!-- Onglets des jours -->
      <div class="day-tabs">
        <button
          v-for="(day, idx) in visibleDays"
          :key="day.toISOString()"
          class="day-tab"
          :class="{
            'day-tab-active': idx === activeDayIndex,
            'day-tab-today': isToday(day)
          }"
          @click="activeDayIndex = idx"
        >
          <span class="tab-weekday">{{ formatDayTab(day).weekday }}</span>
          <span class="tab-date">{{ formatDayTab(day).date }}</span>
          <span class="tab-count">{{ getSlotsForDay(day).length }}</span>
        </button>
      </div>

      <!-- Créneaux du jour actif -->
      <div class="slots-panel">
        <div v-if="activeDaySlots.length > 0" class="slots-grid">
          <button
            v-for="slot in activeDaySlots"
            :key="slot"
            class="slot-btn"
            :class="{ 'slot-selected': appointmentStore.selectedSlot === slot }"
            @click="selectSlot(slot)"
          >
            {{ formatTime(slot) }}
          </button>
        </div>
        <div v-else class="no-slots-day">
          <i class="fas fa-calendar-times"></i>
          <span>{{ t('booking.calendar.noSlotsDay') }}</span>
        </div>
      </div>
    </template>

    <!-- Aucun jour disponible cette semaine -->
    <div v-else-if="!appointmentStore.isLoading" class="no-slots-msg">
      <i class="fas fa-calendar-times"></i>
      <p>
        {{ t('booking.calendar.noSlotsWeek') }}<br />
        {{ t('booking.calendar.tryNextWeek') }}
      </p>
    </div>

    <!-- Erreur -->
    <div v-if="appointmentStore.error" class="calendar-error">
      <i class="fas fa-exclamation-circle"></i>
      {{ appointmentStore.error }}
    </div>
  </div>
</template>

<style scoped>
.week-calendar {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* Navigation */
.week-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.week-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-primary, #1a1a1a);
  text-align: center;
  flex: 1;
}

.week-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.18s;
  flex-shrink: 0;
}

.week-nav-btn:hover:not(:disabled) {
  background: var(--primary, #dc2626);
  color: white;
  border-color: var(--primary, #dc2626);
}

.week-nav-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* Onglets des jours */
.day-tabs {
  display: flex;
  gap: 4px;
  overflow-x: auto;
  scrollbar-width: none;
  padding-bottom: 2px;
}

.day-tabs::-webkit-scrollbar {
  display: none;
}

.day-tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 0.5rem 0.6rem;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  background: white;
  cursor: pointer;
  transition: all 0.18s;
  min-width: 58px;
  flex-shrink: 0;
  position: relative;
}

.day-tab:hover:not(.day-tab-empty) {
  border-color: var(--primary, #dc2626);
  background: rgba(220, 38, 38, 0.04);
}

.day-tab-active {
  border-color: var(--primary, #dc2626) !important;
  background: var(--primary, #dc2626) !important;
}

.day-tab-active .tab-weekday,
.day-tab-active .tab-date {
  color: white !important;
}

.day-tab-active .tab-count {
  background: rgba(255, 255, 255, 0.25) !important;
  color: white !important;
}

.day-tab-today:not(.day-tab-active) {
  border-color: rgba(220, 38, 38, 0.4);
}

.day-tab-today:not(.day-tab-active) .tab-weekday {
  color: var(--primary, #dc2626);
}

.day-tab-empty {
  opacity: 0.4;
  cursor: default;
}

.tab-weekday {
  font-size: 0.7rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: capitalize;
  line-height: 1;
}

.tab-date {
  font-size: 0.68rem;
  color: #9ca3af;
  line-height: 1;
}

.tab-count {
  margin-top: 2px;
  background: rgba(220, 38, 38, 0.1);
  color: var(--primary, #dc2626);
  font-size: 0.62rem;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 10px;
  line-height: 1.4;
}

/* Panneau créneaux */
.slots-panel {
  background: #fafafa;
  border: 1.5px solid #f0f0f0;
  border-radius: 10px;
  padding: 0.75rem;
  min-height: 80px;
}

.slots-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.slot-btn {
  padding: 0.45rem 0.75rem;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--primary, #dc2626);
  background: white;
  border: 1.5px solid rgba(220, 38, 38, 0.25);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
  min-width: 64px;
  text-align: center;
}

.slot-btn:hover {
  background: var(--primary, #dc2626);
  color: white;
  border-color: var(--primary, #dc2626);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.2);
}

.slot-btn.slot-selected {
  background: var(--primary, #dc2626);
  color: white;
  border-color: var(--primary, #dc2626);
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
}

.no-slots-day {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  padding: 1.25rem;
  color: #9ca3af;
  font-size: 0.85rem;
}

/* États globaux */
.calendar-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1.5rem;
  color: #9ca3af;
  font-size: 0.88rem;
}

.calendar-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #fef2f2;
  border-radius: 8px;
  color: #dc2626;
  font-size: 0.85rem;
}

.no-slots-msg {
  text-align: center;
  padding: 1.5rem;
  color: #9ca3af;
  font-size: 0.88rem;
}

.no-slots-msg i {
  font-size: 1.75rem;
  display: block;
  margin-bottom: 0.5rem;
  opacity: 0.4;
}

.no-slots-msg p {
  margin: 0;
  line-height: 1.5;
}
</style>
